// Shared "Save to Playbox" routine, used by both the MainMenu item
// (MainMenuPlbxItems) and the global Cmd/Ctrl+S hotkey (PlbxSaveHotkey) so the
// two paths behave identically. Kept out of the React tree because the hotkey
// lives in an always-mounted component while the menu item unmounts with the
// (Radix) dropdown.

import {
  createScene,
  listWorkspaces,
  putSceneContent,
  type Scene,
} from "./plbxApi";

// Minimal slice of the Excalidraw imperative API we touch here.
type ExcalidrawAPIish = {
  getSceneElementsIncludingDeleted: () => readonly any[];
  getAppState: () => any;
  getFiles: () => any;
  getName?: () => string;
  setToast: (toast: { message: string } | null) => void;
};

// Module-level lock — one in-flight save at a time across all call sites.
let saving = false;

/**
 * Persist the current canvas to the Playbox backend.
 *
 *  - Board already opened from the API (`window.__plbxBoardId` set): save in
 *    place — no rename prompt, no redirect.
 *  - Fresh canvas on `/`: prompt for a name, create the scene, then redirect to
 *    `/` so the new id is wired up for subsequent autosaves.
 *  - Empty name in the prompt: cancelled, nothing saved.
 *
 * Drives the same toast + `plbx:autosave` status the autosave loop uses, so the
 * board-title pill stays consistent after a manual save.
 */
export async function saveCurrentBoard(
  excalidrawAPI: ExcalidrawAPIish | null,
): Promise<void> {
  if (!excalidrawAPI || saving) return;
  saving = true;
  const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
  const appState = excalidrawAPI.getAppState();
  const files = excalidrawAPI.getFiles();

  try {
    const existingId: string | undefined = (window as any).__plbxBoardId;
    let scene: Scene;
    if (existingId) {
      scene = { id: existingId } as Scene;
    } else {
      const suggested = excalidrawAPI.getName?.() || "Untitled board";
      const name = window.prompt(
        "Name this board:",
        suggested === "Excalidraw" ? "Untitled board" : suggested,
      );
      if (!name || !name.trim()) {
        excalidrawAPI.setToast({ message: "Save cancelled" });
        return;
      }
      // Resolve target workspace: prefer the /boards selection, else the
      // caller's personal workspace.
      let workspaceId = localStorage.getItem("plbx_active_workspace") || "";
      if (!workspaceId) {
        const wss = await listWorkspaces();
        const personal = wss.data.find((w) => w.kind === "personal");
        workspaceId = personal?.id ?? wss.data[0]?.id ?? "";
      }
      if (!workspaceId) {
        excalidrawAPI.setToast({
          message: "Save failed: no workspace available",
        });
        return;
      }
      scene = await createScene({ name: name.trim(), workspaceId });
    }

    await putSceneContent(scene.id, {
      type: "excalidraw",
      version: 2,
      source: window.location.origin,
      elements: elements as any[],
      appState: appState as any,
      files: files as any,
    });

    // Keep the board-title autosave pill in sync after a manual save.
    (window as any).__plbxSaveStatus = "saved";
    window.dispatchEvent(
      new CustomEvent("plbx:autosave", { detail: { status: "saved" } }),
    );

    excalidrawAPI.setToast({
      message: existingId ? "Saved" : "Saved — opening board",
    });

    if (!existingId) {
      localStorage.setItem("plbx_pending_board", scene.id);
      localStorage.setItem("plbx_active_board", scene.id);
      window.location.assign("/");
    }
  } catch (e: any) {
    excalidrawAPI.setToast({
      message: `Save failed: ${e?.message || "unknown error"}`,
    });
  } finally {
    saving = false;
  }
}

export default saveCurrentBoard;
